import { useEffect, useState } from 'react';
import { useRouter, useSearch } from '@tanstack/react-router';
import { Lock, Mail, AlertCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, ApiError, API_URL } from '@/lib/api';
import {
  loginPasswordSchema,
  type LoginPasswordForm,
  magicLinkSchema,
  type MagicLinkForm,
} from '@/lib/schemas';

/**
 * /login — порт frontend/src/app/login/page.tsx (AntD → shadcn).
 *
 * Standalone-страница (БЕЗ AppShell), Earth Tech, две вкладки:
 *   1) По паролю   → POST /api/v1/auth/login → 2FA / select-tenant / dashboard
 *   2) По magic-link → POST /api/v1/auth/request-link → /auth/link-sent
 *
 * Поведение разводящей логики (must_setup_2fa / platform_role / memberships)
 * 1:1 повторяет AntD-версию (юзер 2026-04-27, AK-32).
 *
 * Валидация форм через react-hook-form + zod (lib/schemas/auth.ts) —
 * закрывает Nielsen H5 (Error prevention).
 */

interface MeLite {
  user: { platform_role?: string | null };
  memberships?: unknown[];
  must_setup_2fa?: boolean;
}

function PasswordLoginForm({ initialEmail }: { initialEmail: string }) {
  const router = useRouter();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    trigger,
    clearErrors,
    formState: { errors, isSubmitting, isValid },
  } = useForm<LoginPasswordForm>({
    resolver: zodResolver(loginPasswordSchema),
    // AK-465: mode='onChange' обязателен — с 'onBlur' валидация запускается
    // только на blur, и `isValid` не переключается пока пользователь не увёл
    // фокус с последнего поля. Кнопка «Войти» = `disabled={!isValid}` → без
    // явного blur (Playwright page.fill / Chrome MCP автоматизация /
    // пользователь, жмущий Enter в пароле сразу после ввода без Tab-а) она
    // остаётся заблокирована навсегда — submit не долетает до сети, форма
    // «залипает». 'onChange' валидирует на каждом keystroke → isValid
    // становится true как только оба поля прошли zod → кнопка активируется
    // сразу, без обязательного blur. Совпадает с UX-принципом «feedback
    // мгновенный» (см. корневой CLAUDE.md § Принципы UX).
    mode: 'onChange',
    defaultValues: { email: initialEmail, password: '' },
  });

  // AK-469: почта могла приехать из ?email= (протухший аккаунт в свитчере).
  // Прогоняем валидацию сразу — иначе `isValid` пересчитается только после
  // касания поля и кнопка «Войти» залипнет (грабля AK-465). Валидацию по
  // одному полю RHF в `isValid` не поднимает, поэтому гоняем всю форму и
  // тут же убираем сообщения: «Пароль обязателен» до первого ввода — это
  // ошибка до действия, а не подсказка.
  useEffect(() => {
    if (initialEmail) void trigger().then(() => clearErrors());
  }, [initialEmail, trigger, clearErrors]);

  const onSubmit = async (data: LoginPasswordForm) => {
    setServerError(null);
    try {
      const resp = await api.post<
        | { requires_2fa: true; challenge_token: string }
        | MeLite
      >('/api/v1/auth/login', data);

      if ('requires_2fa' in resp && resp.requires_2fa) {
        window.location.href = `/auth/2fa?token=${encodeURIComponent(resp.challenge_token)}`;
        return;
      }

      const me = resp as MeLite;

      if (me.must_setup_2fa) {
        router.navigate({ to: '/auth/2fa-required' });
        return;
      }

      const hasPA = !!me.user.platform_role;
      const mbs = me.memberships ?? [];
      const hasTenants = mbs.length > 0;

      if (hasPA && hasTenants) {
        window.location.href = '/auth/select-context';
      } else if (hasPA) {
        window.location.href = '/platform-admin';
      } else if (mbs.length > 1) {
        window.location.href = '/auth/select-tenant';
      } else if (hasTenants) {
        router.navigate({ to: '/dashboard' });
      } else {
        window.location.href = '/auth/error?reason=no_access';
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err.detail || 'Ошибка входа');
      } else {
        setServerError('Сетевая ошибка');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" autoComplete="on">
      {serverError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{serverError}</AlertDescription>
        </Alert>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="login-email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="login-email"
            type="email"
            placeholder="you@company.ru"
            autoComplete="email"
            aria-invalid={!!errors.email}
            className="pl-9 h-10"
            {...register('email')}
          />
        </div>
        {errors.email && (
          <p className="text-destructive text-xs mt-1">{errors.email.message}</p>
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="login-password">Пароль</Label>
        <PasswordInput
          id="login-password"
          /* Почта уже подставлена — курсор сразу в пароль. */
          autoFocus={!!initialEmail}
          autoComplete="current-password"
          aria-invalid={!!errors.password}
          className="pl-9 h-10"
          startAdornment={
            <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
          }
          {...register('password')}
        />
        {errors.password && (
          <p className="text-destructive text-xs mt-1">{errors.password.message}</p>
        )}
      </div>
      <Button
        type="submit"
        disabled={isSubmitting || !isValid}
        className="w-full"
        size="lg"
      >
        {isSubmitting ? 'Входим…' : 'Войти'}
      </Button>
      <div className="text-right">
        <a
          href="/auth/forgot-password"
          className="text-xs text-primary hover:underline"
        >
          Забыли пароль?
        </a>
      </div>
    </form>
  );
}

function MagicLinkForm({ initialEmail }: { initialEmail: string }) {
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting, isValid },
  } = useForm<MagicLinkForm>({
    resolver: zodResolver(magicLinkSchema),
    // AK-465: см. комментарий в PasswordLoginForm — та же логика.
    mode: 'onChange',
    defaultValues: { email: initialEmail },
  });

  useEffect(() => {
    if (initialEmail) void trigger();
  }, [initialEmail, trigger]);

  const onSubmit = async (data: MagicLinkForm) => {
    setServerError(null);
    try {
      const resp = await fetch(`${API_URL}/api/v1/auth/request-link`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (resp.ok || resp.status === 429) {
        window.location.href = `/auth/link-sent?email=${encodeURIComponent(data.email)}`;
      } else {
        setServerError('Ошибка. Попробуйте ещё раз.');
      }
    } catch {
      setServerError('Сетевая ошибка');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {serverError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{serverError}</AlertDescription>
        </Alert>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="magic-email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="magic-email"
            type="email"
            placeholder="you@company.ru"
            autoComplete="email"
            aria-invalid={!!errors.email}
            className="pl-9 h-10"
            {...register('email')}
          />
        </div>
        {errors.email && (
          <p className="text-destructive text-xs mt-1">{errors.email.message}</p>
        )}
      </div>
      <Button
        type="submit"
        disabled={isSubmitting || !isValid}
        className="w-full"
        size="lg"
      >
        {isSubmitting ? 'Отправляем…' : 'Получить ссылку для входа'}
      </Button>
    </form>
  );
}

export function LoginPage() {
  // AK-469: `/login?email=…` — приходим сюда из свитчера аккаунтов, когда
  // сессия привязанного аккаунта протухла. Подставляем почту, чтобы не
  // набирать её заново.
  const search = useSearch({ strict: false }) as { email?: string };
  const initialEmail = typeof search.email === 'string' ? search.email : '';

  return (
    <div className="min-h-dvh flex items-center justify-center bg-secondary/40 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold">ИИ-Колхоз</CardTitle>
          <CardDescription>Вход в систему</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="password" className="w-full">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="password">По паролю</TabsTrigger>
              <TabsTrigger value="link">По ссылке на email</TabsTrigger>
            </TabsList>
            <TabsContent value="password">
              <PasswordLoginForm initialEmail={initialEmail} />
            </TabsContent>
            <TabsContent value="link">
              <MagicLinkForm initialEmail={initialEmail} />
            </TabsContent>
          </Tabs>
          <div className="mt-6 pt-4 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">
              Первый раз здесь? Попросите админа вашей компании пригласить вас.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default LoginPage;
